import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import * as Sentry from "@sentry/react";
import { auth, db } from './config/firebase';
import { useAuthStore } from './store/authStore';
import { AuthLayout } from './components/auth/AuthLayout';
import { ToastProvider } from './context/ToastContext';
import { PostHogWrapper } from './context/PostHogContext';
import { BrandProvider } from './contexts/BrandContext';
import { initPostHog } from './config/posthog';
import { identifyUser, trackPageView } from './utils/analytics';
import { CrispChat } from './components/common/CrispChat';
import { BetaNotificationBar } from './components/common/BetaNotificationBar';
import './i18n';
import { useTranslation } from 'react-i18next';
import { sendEmailVerification } from './utils/firebaseAuth';

import Sidebar from './components/layout/Sidebar';
import Analytics from './pages/Analytics';
import Brands from './pages/Brands';
import BrandForm from './pages/BrandForm';
import Billing from './pages/Billing';
import Accounts from './pages/Accounts';
import TeamMembers from './pages/TeamMembers';
import Settings from './pages/Settings';
import Personas from './pages/Personas';
import Calendar from './pages/Calendar';
import ContentCreation from './pages/ContentCreation';
import MediaGallery from './pages/MediaGallery';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import { CreateEditPostPage } from './pages/CreateEditPostPage';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import Onboarding from './pages/Onboarding';
import MyContentHub from './pages/MyContentHub';
import Dashboard from './pages/Dashboard';
import Integrations from './pages/Integrations';
import Links from './pages/Links';
import { User } from './types';

const queryClient = new QueryClient();

// Initialize PostHog immediately
initPostHog().catch(err => {
  console.error('Failed to initialize PostHog:', err);
});

// Add a new component for email verification prompt
const EmailVerificationPrompt = () => {
  const [isResending, setIsResending] = useState(false);
  const { t } = useTranslation();
  
  const handleResendVerification = async () => {
    if (!auth.currentUser) return;
    
    setIsResending(true);
    try {
      await sendEmailVerification(auth.currentUser);
      alert(t('auth.verification.resendSuccess'));
    } catch (error) {
      console.error('Error sending verification email:', error);
      alert(t('auth.verification.resendError'));
    } finally {
      setIsResending(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
        <div className="text-center">
          <img
            src="/assets/Socialness-Logo/Socialness-Icon-1024x1024.png"
            alt="Socialness"
            className="mx-auto h-16 w-16"
          />
          <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
            {t('auth.verification.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.verification.subtitle')}
          </p>
        </div>
        
        <div className="mt-8 space-y-6">
          <div className="rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-blue-700">
                  {t('auth.verification.checkInbox')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="text-sm text-center">
            <button
              onClick={handleResendVerification}
              disabled={isResending}
              className="font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
            >
              {isResending 
                ? t('auth.verification.resending')
                : t('auth.verification.resend')
              }
            </button>
          </div>
          
          <div className="text-center">
            <button
              onClick={() => auth.signOut()}
              className="text-sm font-medium text-gray-600 hover:text-gray-500"
            >
              {t('auth.verification.signOut')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder for Firestore user fetching
// Replace with your actual implementation
const getUserDocument = async (userId: string): Promise<Partial<User> | null> => {
  try {
    const userDocRef = doc(db, 'users', userId); 
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      const counts = data.counts || {};
      const onboardingProcess = counts.onboardingProcess;
      
      // Extract firstName and lastName, potentially deriving from name if needed
      let firstName = data.firstName;
      let lastName = data.lastName;
      // Basic fallback: if no firstName/lastName, try splitting name
      if ((!firstName || !lastName) && data.name) {
         const nameParts = data.name.split(' ');
         firstName = firstName || nameParts[0];
         lastName = lastName || nameParts.slice(1).join(' ');
      }
      
      return {
        ...data, // Spread all data from Firestore
        id: userSnap.id,
        firstName, // Explicitly include possibly derived firstName
        lastName, // Explicitly include possibly derived lastName
        counts: {
           ...counts,
           onboardingProcess: typeof onboardingProcess === 'number' ? onboardingProcess : 0,
        }
      } as Partial<User>; 
    } else {
      console.log("No such user document!", userId);
      return null; 
    }
  } catch (error) {
    console.error("Error fetching user document:", error);
    return null;
  }
};

// Component to handle redirection based on onboarding status
const AuthRedirector = () => {
  const { user, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    // Optional: Show a loading spinner instead of null
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check if user exists and onboardingProcess is available
  const onboardingProcess = user?.counts?.onboardingProcess;

  if (typeof onboardingProcess === 'number') {
    if (onboardingProcess < 100) {
      // Redirect to onboarding, preserving any state from previous location if needed
      return <Navigate to="/onboarding" state={{ from: location }} replace />;
    } else {
      // Redirect to dashboard
      return <Navigate to="/dashboard" state={{ from: location }} replace />;
    }
  } else {
    // Fallback: If onboarding status is unknown (e.g., error fetching), go to dashboard or a default page.
    // Or potentially redirect to onboarding as a safe default if user exists but counts are missing.
    console.warn('Onboarding status unknown, defaulting to dashboard.');
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }
};

// PublicRoute remains the same for now, redirecting logged-in users away from auth pages
const PublicRoute = () => {
  const { user, isLoading } = useAuthStore(); 
  if (isLoading) { 
    return null; 
  }
  // Redirect to root, which will then handle onboarding/dashboard redirect
  return user ? <Navigate to="/" replace /> : <Outlet />; 
};

// AppContent component to use hooks inside Router context
const AppContent = () => {
  const { setUser, setLoading, user } = useAuthStore();
  const [emailVerified, setEmailVerified] = useState(true);
  const { t, i18n } = useTranslation();
  
  useDocumentTitle('Socialness.ai - Social Media Management');

  // Track page views
  useEffect(() => {
    trackPageView(window.location.pathname);
    
    // Listen for route changes
    const handleRouteChange = () => {
      trackPageView(window.location.pathname);
    };
    
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    setLoading(true); 
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        // Set Sentry user
        Sentry.setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || undefined,
        });

        const userDoc = await getUserDocument(firebaseUser.uid);
        
        const baseUserData = {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || userDoc?.name || '', 
          photoURL: firebaseUser.photoURL || userDoc?.photoURL || '',
          role: userDoc?.role || 'writer', 
        };

        // Merge auth data with Firestore data (including counts, firstName, lastName)
        const mergedUserData: User = {
          ...baseUserData,
          // Spread Firestore doc, potentially overwriting name/photoURL if they exist there
          ...userDoc, 
          id: baseUserData.id, // Ensure auth ID is primary
          // Explicitly include fields potentially derived in getUserDocument
          firstName: userDoc?.firstName || baseUserData.name.split(' ')[0] || '', 
          lastName: userDoc?.lastName || baseUserData.name.split(' ').slice(1).join(' ') || '',
          counts: {
            onboardingProcess: userDoc?.counts?.onboardingProcess ?? 0,
            ...(userDoc?.counts || {}), 
          }
        };
        
                  // Debug log and runtime check for required fields
          if (!mergedUserData.id || !mergedUserData.email || !mergedUserData.name) {
          console.error('User object missing required fields:', mergedUserData);
        }
        setUser(mergedUserData);
        setEmailVerified(firebaseUser.emailVerified);
        
        identifyUser(firebaseUser.uid, {
          email: mergedUserData.email,
          name: mergedUserData.name
        });
      } else {
        // Clear Sentry user on logout
        Sentry.setUser(null);
        setUser(null);
        setEmailVerified(true); 
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setLoading]);

  if (user && !emailVerified) {
    return <EmailVerificationPrompt />;
  }

  return (
    <AuthLayout>
      <Routes>
        <Route element={<PublicRoute />}>
          {/* English routes */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/signup" element={<SignUp />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          
          {/* French routes */}
          <Route path="/auth/connexion" element={<Login />} />
          <Route path="/auth/inscription" element={<SignUp />} />
          <Route path="/auth/mot-de-passe-oublie" element={<ForgotPassword />} />
        </Route>

        {/* Protected App Routes */}
        <Route
          path="/*"
          element={
             // Use a wrapper component for protected routes that checks auth
             <ProtectedRoute>
                <div className="flex flex-col bg-white min-h-screen">
                  <BetaNotificationBar />
                  <div className="flex flex-1">
                    <Sidebar />
                    <main className="flex-1 bg-white">
                    <Routes>
                      <Route path="/" element={<AuthRedirector />} /> {/* Use AuthRedirector for root */}
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/brand-strategy-lab" element={<Brands />} />
                      <Route path="/brand-strategy-lab/new" element={<BrandForm />} />
                      <Route path="/brand-strategy-lab/:brandId/edit" element={<BrandForm />} />
                      <Route path="/social-media-hub" element={<Accounts />} />
                      <Route path="/accounts" element={<Navigate to="/social-media-hub" replace />} />
                      <Route path="/team" element={<TeamMembers />} />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/ai-personas-builder" element={<Personas />} />
                      <Route path="/calendar" element={<Calendar />} />
                      <Route path="/my-content-hub" element={<MyContentHub />} />
                      <Route path="/content-creation" element={<ContentCreation key={`content-${i18n.language}`} />} />
                      <Route path="/media" element={<MediaGallery />} />
                      <Route path="/posts/new" element={<CreateEditPostPage />} />
                      <Route path="/posts/:postId/edit" element={<CreateEditPostPage />} />
                      <Route path="/integrations" element={<Integrations />} />
                      <Route path="/ai-links-builder" element={<Links />} />
                    </Routes>
                  </main>
                  </div>
                </div>
             </ProtectedRoute>
          }
        />
      </Routes>
    </AuthLayout>
  );
};

// Wrapper for protected routes
const ProtectedRoute: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { user, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    // Show loader while checking auth state
    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
        </div>
    );
  }

  if (!user) {
    // Redirect to login if not authenticated
    const redirectTo = `/auth/login${location.search}`;
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render child routes if authenticated
  return <>{children}</>; 
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <PostHogWrapper>
          <BrandProvider>
            <Router>
              <AppContent />
              <CrispChat />
            </Router>
          </BrandProvider>
        </PostHogWrapper>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;